import React, { useState, useEffect, useRef } from 'react';
import { isBrowser } from 'ws-scripts/modules/environment';
import { usePrefs, useRequestData, useLabels } from 'wsm-common-data';
import { useSelector } from 'react-redux';
import { galleryTrackingEvents } from '../../utilities/tracking';

import ImageSlider from '../common/ImageSlider';
import RightHandImage from '../common/RightHandImage';
import GalleryModal from './GalleryModal';
import GalleryToolbar from './GalleryToolbar';
import NoImage from '../common/NoImage';
import useWiapiVehicleMedia from '../../hooks/useWiapiVehicleMedia';
import useMedia from '../../hooks/useMedia';

const GalleryContainer = () => {
	// 0: Select photos
	// 1: Select video
	// 2: Select spin 360 (Out of scope)
	const primarySliderRef = useRef(null);
	const { widgetName, windowId } = useRequestData();
	const prefs = usePrefs();
	const labels = useLabels();

	const [activeToolBar, setActiveToolbar] = useState(0);
	const [openModal, setOpenModal] = useState(false);
	const [carouselIndex, setCarouselIndex] = useState(0);

	const isDesktop = useMedia('(min-width: 576px)', true);
	// set position for gallery toolbar
	const { galleryToolbarPlacement = 'top-left' } = prefs;
	const { imgThumbStyle } = prefs;
	const { imagesToDisplay } = useSelector((state) => state.media);
	const { vehicle } = useSelector((state) => state.widgetInfo);
	const {
		make,
		year,
		model,
		bodyStyle,
		trim,
		type,
		certified,
		imgAttributes
	} = vehicle;
	const pubsub = isBrowser && window?.DDC?.pubsub;
	let totalImages = imagesToDisplay.length;

	// Apply max-width constraint for full width gallery layout on desktop
	const { widgetClasses } = prefs;
	const classNames =
		widgetClasses.includes('full-width-gallery') &&
		isDesktop &&
		(imgThumbStyle === 'horizontal' || totalImages === 1)
			? 'max-w-85vh'
			: '';

	const typeTerm = type ? labels.get(certified ? 'CERTIFIED' : type) : '';
	const typeTermCapitalCase = typeTerm
		? typeTerm[0].toUpperCase() + typeTerm.substr(1).toLowerCase()
		: '';
	const imgTitle = imgAttributes
		? imgAttributes?.imageTitle
		: `${typeTermCapitalCase} ${year} ${make} ${model} ${trim} ${bodyStyle}`.trim();

	if (
		totalImages === 1 &&
		imagesToDisplay[0].src?.includes('unavailable_stockphoto')
	) {
		totalImages = 0;
	}

	const { widgetElement } = useWiapiVehicleMedia({
		carouselIndex,
		setCarouselIndex,
		openModal
	});

	useEffect(() => {
		if (pubsub) {
			pubsub?.publish('ws-vehicle-gallery/modal-open', openModal);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [openModal]);

	useEffect(() => {
		if (pubsub && totalImages > 0) {
			// logger.info(imagesToDisplay[0].thumbnail)
			pubsub?.publishDelayed(
				'ws-vehicle-toolbar/show-image',
				imagesToDisplay[0].thumbnail
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [totalImages]);

	if (totalImages === 0) {
		return <NoImage />;
	}

	const onOpenModal = () => {
		// add overflow hidden for body
		const bodyContainer = document.body;
		bodyContainer.classList.add('overflow-hidden');
		setOpenModal(true);
	};

	const onClose = () => {
		galleryTrackingEvents.closeModal(widgetName, windowId);

		// remove overflow hidden for body
		const bodyContainer = document.body;
		bodyContainer.classList.remove('overflow-hidden');
		// Update the current slide for primary slider
		setTimeout(() => {
			if (
				primarySliderRef.current &&
				primarySliderRef.current?.slickGoTo
			) {
				primarySliderRef.current.slickGoTo(carouselIndex, true);
			}
		}, 400);
		setOpenModal(false);
		// set activeToolBar default 0 when close modal
		setActiveToolbar(0);
	};

	if (pubsub) {
		pubsub.subscribe(
			'ws-vehicle-gallery/close-vehicle-gallery',
			(isGalleryCTABtnClicked) => {
				if (isGalleryCTABtnClicked) {
					onClose();
				}
			}
		);
	}

	return (
		<div
			className={`position-relative ${
				!isDesktop ? 'mx-n4' : ''
			} ${classNames}`}
			data-location="vehicle-media-container"
			data-testid="gallery-container"
		>
			<div className="overflow-auto">
				{imgThumbStyle === 'horizontal' ? (
					<>
						{galleryToolbarPlacement === 'top-left' && (
							<GalleryToolbar
								onOpenModal={onOpenModal}
								setActiveToolbar={setActiveToolbar}
							/>
						)}
						<ImageSlider
							onOpenModal={onOpenModal}
							setCarouselIndex={setCarouselIndex}
							carouselIndex={carouselIndex}
							sliderRef={primarySliderRef}
							activeToolBar={activeToolBar}
							widgetElement={widgetElement}
							imgTitle={imgTitle}
						/>
						{galleryToolbarPlacement === 'bottom-left' && (
							<GalleryToolbar
								onOpenModal={onOpenModal}
								setActiveToolbar={setActiveToolbar}
							/>
						)}
					</>
				) : (
					<>
						<RightHandImage
							onOpenModal={onOpenModal}
							setCarouselIndex={setCarouselIndex}
							setActiveToolbar={setActiveToolbar}
							imgTitle={imgTitle}
						/>
						<GalleryToolbar
							onOpenModal={onOpenModal}
							setActiveToolbar={setActiveToolbar}
						/>
					</>
				)}
			</div>
			<GalleryModal
				onClose={onClose}
				openModal={openModal}
				carouselIndex={carouselIndex}
				setCarouselIndex={setCarouselIndex}
				activeToolBar={activeToolBar}
				setActiveToolbar={setActiveToolbar}
				imgTitle={imgTitle}
			/>
		</div>
	);
};

export default GalleryContainer;
